import { EventData, EventDataTypeEnum } from "../store/services/timelineApiRTK";
import {
  ProcessedEventData,
  CommentEventData,
  FileAttachmentEventData,
  SurveyEventData,
  EmailEventData,
  ParsedEventData,
} from "../types/EventData.types";

// Safe JSON parser
const safeParseJSON = <T>(jsonString: string): T | null => {
  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.warn("Failed to parse EventData JSON:", error);
    return null;
  }
};

// Parse specific event data types
const parseCommentData = (data: string): CommentEventData | null => {
  return safeParseJSON<CommentEventData>(data);
};

const parseFileAttachmentData = (
  data: string
): FileAttachmentEventData | null => {
  return safeParseJSON<FileAttachmentEventData>(data);
};

const parseSurveyData = (data: string): SurveyEventData | null => {
  return safeParseJSON<SurveyEventData>(data);
};

const parseEmailData = (data: string): EmailEventData | null => {
  return safeParseJSON<EmailEventData>(data);
};

// Display info registry for all event types
const eventDisplayRegistry: Record<
  number,
  { displayName: string; icon: string; color: string }
> = {
  // Already fully mapped types (with custom rendering)
  [EventDataTypeEnum.Text]: { displayName: "Kommentar", icon: "Comment", color: "#1976d2" },
  [EventDataTypeEnum.IncomingEmail]: { displayName: "Inkommande e-post", icon: "Email", color: "#1565c0" },
  [EventDataTypeEnum.OutgoingEmail]: { displayName: "Utgående e-post", icon: "Send", color: "#2e7d32" },
  [EventDataTypeEnum.UnprocessedEmail]: { displayName: "Obearbetad e-post", icon: "EmailOutlined", color: "#f57c00" },
  [EventDataTypeEnum.SurveyResponse]: { displayName: "Enkät", icon: "Quiz", color: "#ed6c02" },

  // File types
  [EventDataTypeEnum.FileConfirmationSent]: { displayName: "Bekräftelse skickad", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.FileInvoiceSent]: { displayName: "Faktura skickad", icon: "Receipt", color: "#2e7d32" },
  [EventDataTypeEnum.FileCombinedClaim]: { displayName: "Samlat krav", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.ContractFile]: { displayName: "Avtalsfil", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.ContractFiles]: { displayName: "Avtalsfiler", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.CustomerDocument]: { displayName: "Kunddokument", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.CustomerPoaDocument]: { displayName: "Fullmaktsdokument", icon: "Description", color: "#2e7d32" },

  // Communication types
  [EventDataTypeEnum.Call]: { displayName: "Samtal", icon: "Phone", color: "#7b1fa2" },
  [EventDataTypeEnum.OutgoingCall]: { displayName: "Utgående samtal", icon: "PhoneForwarded", color: "#7b1fa2" },
  [EventDataTypeEnum.OutgoingSms]: { displayName: "Utgående SMS", icon: "Sms", color: "#7b1fa2" },
  [EventDataTypeEnum.AppPush]: { displayName: "Push-notis", icon: "Notifications", color: "#7b1fa2" },
  [EventDataTypeEnum.IncomingAppMessage]: { displayName: "Inkommande appmeddelande", icon: "MobileScreenShare", color: "#1565c0" },
  [EventDataTypeEnum.OutgoingAppMessage]: { displayName: "Utgående appmeddelande", icon: "PhoneAndroid", color: "#2e7d32" },
  [EventDataTypeEnum.SendMail]: { displayName: "Utskick", icon: "Send", color: "#2e7d32" },
  [EventDataTypeEnum.SendMailPdf]: { displayName: "Utskick (PDF)", icon: "PictureAsPdf", color: "#2e7d32" },
  [EventDataTypeEnum.ForwardedEmail]: { displayName: "Vidareskickad e-post", icon: "Forward", color: "#1565c0" },
  [EventDataTypeEnum.UnprocessedSpamEmail]: { displayName: "Spam", icon: "ReportGmailerrorred", color: "#d32f2f" },
  [EventDataTypeEnum.Apsis]: { displayName: "Apsis-utskick", icon: "Campaign", color: "#ed6c02" },
  [EventDataTypeEnum.MoveOutReminderCommunication]: { displayName: "Flyttpåminnelse", icon: "MarkunreadMailbox", color: "#ed6c02" },

  // CRM / Sales
  [EventDataTypeEnum.Opportunity]: { displayName: "Affärsmöjlighet", icon: "TrendingUp", color: "#1976d2" },
  [EventDataTypeEnum.Lead]: { displayName: "Lead", icon: "PersonAdd", color: "#1976d2" },
  [EventDataTypeEnum.LeadsData]: { displayName: "Lead-data", icon: "PersonAdd", color: "#1976d2" },
  [EventDataTypeEnum.CustomerNote]: { displayName: "Kundanteckning", icon: "StickyNote2", color: "#1976d2" },
  [EventDataTypeEnum.SuggestedCustomerContent]: { displayName: "Kundförslag", icon: "Lightbulb", color: "#ed6c02" },
  [EventDataTypeEnum.TrustPilotReview]: { displayName: "TrustPilot-omdöme", icon: "Star", color: "#ed6c02" },

  // Contract types
  [EventDataTypeEnum.ContractProductConfiguratorData]: { displayName: "Avtalskonfiguration", icon: "Settings", color: "#455a64" },
  [EventDataTypeEnum.RenewContractProductConfiguratorData]: { displayName: "Förnyelsedata", icon: "Autorenew", color: "#455a64" },
  [EventDataTypeEnum.ContractProductConfiguratorCreateDraft]: { displayName: "Avtalsutkast", icon: "DraftsOutlined", color: "#455a64" },
  [EventDataTypeEnum.ContractProductConfigurationRenewDraft]: { displayName: "Förnyelseutkast", icon: "DraftsOutlined", color: "#455a64" },
  [EventDataTypeEnum.RenewContractFixedVolumeData]: { displayName: "Förnyelse fast volym", icon: "Autorenew", color: "#455a64" },
  [EventDataTypeEnum.WinbackContract]: { displayName: "Winback-avtal", icon: "Replay", color: "#455a64" },
  [EventDataTypeEnum.ContractCancellation]: { displayName: "Uppsägning", icon: "Cancel", color: "#d32f2f" },

  // Customer updates
  [EventDataTypeEnum.CustomerAddressUpdate]: { displayName: "Adressändring", icon: "LocationOn", color: "#455a64" },
  [EventDataTypeEnum.Discount]: { displayName: "Rabatt", icon: "LocalOffer", color: "#ed6c02" },
  [EventDataTypeEnum.ExternalUserLogin]: { displayName: "Extern inloggning", icon: "Login", color: "#455a64" },

  // External sales
  [EventDataTypeEnum.ExternalSaleRequestRaw]: { displayName: "Extern försäljning", icon: "ShoppingCart", color: "#2e7d32" },
  [EventDataTypeEnum.ExternalSaleFiles]: { displayName: "Extern försäljning (filer)", icon: "ShoppingCart", color: "#2e7d32" },
  [EventDataTypeEnum.ExternalSaleSignAgreement]: { displayName: "Extern signering", icon: "Draw", color: "#2e7d32" },
  [EventDataTypeEnum.ExternalSalePayout]: { displayName: "Extern utbetalning", icon: "Payments", color: "#2e7d32" },

  // Financial
  [EventDataTypeEnum.SoftReminderClaim]: { displayName: "Mjuk påminnelse", icon: "NotificationsActive", color: "#ed6c02" },
  [EventDataTypeEnum.DirectDebitRequest]: { displayName: "Autogiro-begäran", icon: "AccountBalance", color: "#455a64" },
  [EventDataTypeEnum.UseDirectDebit]: { displayName: "Autogiro", icon: "AccountBalance", color: "#455a64" },
  [EventDataTypeEnum.SwishCustomerPayout]: { displayName: "Swish-utbetalning", icon: "Payments", color: "#2e7d32" },
  [EventDataTypeEnum.MarkavtalSwishOut]: { displayName: "Markavtal Swish", icon: "Payments", color: "#2e7d32" },
  [EventDataTypeEnum.OpenEInvoiceBank]: { displayName: "E-faktura", icon: "Receipt", color: "#455a64" },
  [EventDataTypeEnum.NegativeAperak]: { displayName: "Negativ Aperak", icon: "Warning", color: "#d32f2f" },

  // BankID / POA
  [EventDataTypeEnum.BankIdSignedData]: { displayName: "BankID-signering", icon: "VerifiedUser", color: "#2e7d32" },
  [EventDataTypeEnum.BankIDSignedFile]: { displayName: "BankID-signerad fil", icon: "VerifiedUser", color: "#2e7d32" },
  [EventDataTypeEnum.PoaInformation]: { displayName: "Fullmakt", icon: "Gavel", color: "#455a64" },
  [EventDataTypeEnum.IncomingPoaInformation]: { displayName: "Inkommande fullmakt", icon: "Gavel", color: "#1565c0" },

  // Energy / Product specific
  [EventDataTypeEnum.ChargeBoxInformation]: { displayName: "Laddbox-info", icon: "EvStation", color: "#2e7d32" },
  [EventDataTypeEnum.Z04Content]: { displayName: "Z04-meddelande", icon: "ElectricalServices", color: "#455a64" },
  [EventDataTypeEnum.DalaVind2023]: { displayName: "DalaVind 2023", icon: "Air", color: "#0288d1" },
  [EventDataTypeEnum.DalaVind2024]: { displayName: "DalaVind 2024", icon: "Air", color: "#0288d1" },
  [EventDataTypeEnum.DalaVind2025]: { displayName: "DalaVind 2025", icon: "Air", color: "#0288d1" },
  [EventDataTypeEnum.EnergyMember]: { displayName: "Energimedlem", icon: "Bolt", color: "#0288d1" },
  [EventDataTypeEnum.EnergyMemberTransaction]: { displayName: "Energimedlem transaktion", icon: "Bolt", color: "#0288d1" },
  [EventDataTypeEnum.EnergyMemberPayout]: { displayName: "Energimedlem utbetalning", icon: "Payments", color: "#0288d1" },
  [EventDataTypeEnum.AvestaElpool2024]: { displayName: "Avesta Elpool 2024", icon: "Bolt", color: "#0288d1" },
  [EventDataTypeEnum.SecureVolume]: { displayName: "Säkrad volym", icon: "Lock", color: "#2e7d32" },
  [EventDataTypeEnum.SecureVolumeContractFile]: { displayName: "Säkrad volym avtal", icon: "Description", color: "#2e7d32" },
  [EventDataTypeEnum.SecureVolumeSignedContractFile]: { displayName: "Säkrad volym signerat avtal", icon: "VerifiedUser", color: "#2e7d32" },
  [EventDataTypeEnum.BrfGruven2025]: { displayName: "BRF Gruven 2025", icon: "Apartment", color: "#455a64" },

  // Jobs / System
  [EventDataTypeEnum.AddedJob]: { displayName: "Jobb tillagt", icon: "Work", color: "#455a64" },
  [EventDataTypeEnum.ErrorJob]: { displayName: "Fel i jobb", icon: "Error", color: "#d32f2f" },
  [EventDataTypeEnum.BrazeEvent]: { displayName: "Braze-händelse", icon: "Campaign", color: "#ed6c02" },
};

// Get display information for event types
const getEventDisplayInfo = (
  type: EventDataTypeEnum,
  parsedData: ParsedEventData | null
) => {
  // Special case for file with filename
  if (type === EventDataTypeEnum.FileCrmDocument) {
    const fileData = parsedData as FileAttachmentEventData;
    return {
      displayName: `Fil: ${fileData?.OrginalFilename || "Okänd fil"}`,
      icon: "AttachFile",
      color: "#2e7d32",
    };
  }

  const registered = eventDisplayRegistry[type];
  if (registered) {
    return registered;
  }

  return {
    displayName: `Händelse (typ ${type})`,
    icon: "Event",
    color: "#757575",
  };
};

// Main parser function
export const parseEventData = (eventData: EventData): ProcessedEventData => {
  let parsedData: ParsedEventData | null = null;

  // Parse data based on type
  switch (eventData.Type) {
    case EventDataTypeEnum.Text:
      parsedData = parseCommentData(eventData.Data);
      break;
    case EventDataTypeEnum.FileCrmDocument:
      parsedData = parseFileAttachmentData(eventData.Data);
      break;
    case EventDataTypeEnum.SurveyResponse:
      parsedData = parseSurveyData(eventData.Data);
      break;
    case EventDataTypeEnum.IncomingEmail:
    case EventDataTypeEnum.OutgoingEmail:
    case EventDataTypeEnum.UnprocessedEmail:
      parsedData = parseEmailData(eventData.Data);
      break;
    default:
      // Try to parse as generic JSON for unknown types
      parsedData = safeParseJSON(eventData.Data);
      break;
  }

  const displayInfo = getEventDisplayInfo(eventData.Type, parsedData);

  return {
    ...eventData,
    parsedData,
    ...displayInfo,
  };
};

// Batch parser for multiple events
export const parseEventDataArray = (
  eventDataArray: EventData[]
): ProcessedEventData[] => {
  return eventDataArray.map(parseEventData);
};

// Utility functions for display
export const getEventDescription = (
  processedEvent: ProcessedEventData
): string => {
  if (!processedEvent.parsedData) {
    return "Ingen beskrivning tillgänglig";
  }

  switch (processedEvent.Type) {
    case EventDataTypeEnum.Text: {
      const commentData = processedEvent.parsedData as CommentEventData;
      return commentData.Description || "Tom kommentar";
    }

    case EventDataTypeEnum.FileCrmDocument: {
      const fileData = processedEvent.parsedData as FileAttachmentEventData;
      return `Fil uppladdad: ${fileData.OrginalFilename}`;
    }

    case EventDataTypeEnum.SurveyResponse: {
      const surveyData = processedEvent.parsedData as SurveyEventData;
      return `Enkät (Instance: ${surveyData.InstanceId})`;
    }

    case EventDataTypeEnum.IncomingEmail:
    case EventDataTypeEnum.OutgoingEmail:
    case EventDataTypeEnum.UnprocessedEmail: {
      const emailData = processedEvent.parsedData as EmailEventData;
      return emailData.Subject || "Inget ämne";
    }

    default:
      return JSON.stringify(processedEvent.parsedData, null, 2);
  }
};

// Get file download URL (placeholder - replace with actual implementation)
export const getFileDownloadUrl = (
  fileData: FileAttachmentEventData
): string => {
  // This should be replaced with actual file download URL logic
  return `/api/files/${fileData.Filename}`;
};
